import { ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { CasingGrade, CasingGradeFiltersResponse } from '../../models/response.model';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ProductDataService } from '../../services/product-data.service';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { Store } from '@ngxs/store';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-casing-grade',
  templateUrl: './casing-grade.component.html',
  styleUrls: ['./casing-grade.component.scss'],
  standalone: false
})
export class CasingGradeComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  gradeList: CasingGrade[] = [];
  gradeFilterList: string[] = [];
  descriptionFilterList: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  isSaveEnabled = false;
  nonDeletableGrades: string[] = [];
  locallyAddedGrades: CasingGrade[] = [];
  itemToDelete: CasingGrade | null = null;
  productSaveDisabled = true;
  isLoading = true;

  tableConfig: TableConfig<CasingGrade> = {
    paginator: {
      type: 'scroll',
      pageSize: 20
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<CasingGrade> = () => {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.generateFilterForm();
    return this.productDataService.getCasingGrades(this.filterFormBackend.value).pipe(
      map((grades: CasingGrade[]) => {
        this.gradeList = Array.isArray(grades) ? grades : [];
        this.getTableRules([...this.locallyAddedGrades, ...this.gradeList]);

        const count = [...this.locallyAddedGrades, ...this.gradeList].length;

        this.updateDisplayedRowsForm([...this.locallyAddedGrades, ...this.gradeList]);
        return {
          items: [...this.locallyAddedGrades, ...this.gradeList],
          count: count
        };
      }),
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      grades: [[]],
      descriptions: [[]]
    });
    this.filterFormBackend = this.fb.group({
      grades: [[]],
      descriptions: [[]]
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      grades: this.filterForm.get('grades')?.value || '',
      descriptions: this.filterForm.get('descriptions')?.value || ''
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.setupFormListeners();
    this.productDataService.getCasingGradeFilters().subscribe((response: CasingGradeFiltersResponse) => {
      this.gradeFilterList = response.data.gradeCodes || [];
      this.descriptionFilterList = response.data.gradeDescriptions || [];
    });
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.checkIfModified();
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        grade: {
          cell: 'grade',
          header: this.translationService.translate('gradeText')
        },
        description: {
          cell: 'description',
          header: this.translationService.translate('descriptionText')
        },
        rank: {
          cell: 'rank',
          header: this.translationService.translate('rankText')
        },
        action: {
          cell: 'action',
          header: this.translationService.translate('actionText')
        }
      };
      this.cdr.markForCheck();
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  updateDisplayedRowsForm(items: CasingGrade[]): void {
    const rowForms = items.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: CasingGrade): FormGroup {
    return this.fb.group({
      casingGradePk: [row.casingGradePk],
      casingGrade: [row.casingGrade, [Validators.required]],
      casingDescription: [row.casingDescription],
      rank: [row.rank, [Validators.required, Validators.min(1), Validators.max(99)]]
    });
  }

  getControl(item: CasingGrade, controlName: keyof CasingGrade): FormControl {
    const combinedItems = [...this.locallyAddedGrades, ...this.gradeList];
    const index = combinedItems.findIndex((grade) => grade.casingGradePk === item.casingGradePk);

    if (index === -1) {
      return new FormControl('');
    }

    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray || formArray.length <= index) {
      return new FormControl('');
    }

    const formGroup = formArray.at(index);
    if (!formGroup) {
      return new FormControl('');
    }

    const control = formGroup.get(controlName) as FormControl;
    return control || new FormControl('');
  }

  get canEditCasingGrade(): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return roles.includes('SETUP_CASING_GRADE_EDIT') && !this.productSaveDisabled;
  }

  checkIfModified(): void {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const combinedItems = [...this.locallyAddedGrades, ...this.gradeList];
    let modified = false;

    for (let i = 0; i < combinedItems.length; i++) {
      const original = combinedItems[i];
      const current = formArray.at(i).value;
      const hasData = current.casingGrade || current.casingDescription || current.rank;
      if (hasData && Object.keys(current).some((key) => current[key] !== original[key])) {
        modified = true;
        break;
      }
    }
    this.isSaveEnabled = modified && this.canEditCasingGrade && !this.productSaveDisabled;
  }

  private validateRank(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    if (!current.rank) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('rankRequiredText')
      };
    }

    if (typeof current.rank === 'string' && isNaN(Number(current.rank))) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('rankInvalidText')
      };
    }

    if (Number(current.rank) < 1 || Number(current.rank) > 99) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('rankInvalidRangeText')
      };
    }

    const duplicateRank = allCurrentValues.find((item) => item.index !== index && item.rank === current.rank);
    if (duplicateRank) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('duplicateRankText')
      };
    }

    return { isValid: true };
  }

  private validateGrade(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    if (!current.casingGrade) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('gradeRequiredText')
      };
    }

    if (typeof current.casingGrade === 'string' && /\d/.test(current.casingGrade)) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('gradeInvalidText')
      };
    }

    const duplicateGrade = allCurrentValues.find(
      (item) => item.index !== index && item.casingGrade.toLowerCase() === current.casingGrade.toLowerCase()
    );
    if (duplicateGrade) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('duplicateGradeText')
      };
    }

    return { isValid: true };
  }

  save(): void {
    if (!this.canEditCasingGrade || !this.isSaveEnabled) {
      return;
    }

    const modifiedRows: CasingGrade[] = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const allFormValues = formArray.value.map((val: any, idx: number) => ({ ...val, index: idx }));

    for (let i = 0; i < formArray.length; i++) {
      const current = formArray.at(i).value;

      if (!current.casingGrade && !current.rank) {
        continue;
      }

      const isValidGrade = this.validateGrade(current, i, allFormValues);
      const isValidRank = this.validateRank(current, i, allFormValues);

      if (!isValidGrade.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidGrade.errorMessage!
        } as Alert);
      }

      if (!isValidRank.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidRank.errorMessage!
        } as Alert);
      }

      if (!isValidGrade.isValid || !isValidRank.isValid) {
        return;
      }
    }

    const totalRows = [...this.locallyAddedGrades, ...this.gradeList];
    for (let i = 0; i < totalRows.length; i++) {
      const original = totalRows[i];
      const current = formArray.at(i).value;

      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...original, ...current });
      }
    }

    if (modifiedRows.length > 0) {
      this.productDataService.updateCasingGrades(modifiedRows).subscribe({
        next: (response: any) => {
          const responseData = response?.data || response;
          if (Array.isArray(responseData)) {
            responseData.forEach((updatedGrade: CasingGrade) => {
              const existingIndex = this.gradeList.findIndex((grade) => grade.casingGradePk === updatedGrade.casingGradePk);
              if (existingIndex !== -1) {
                this.gradeList[existingIndex] = updatedGrade;
              } else {
                this.gradeList.push(updatedGrade);
              }
            });
          }

          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('gradeSavedText')
          } as Alert);

          this.locallyAddedGrades = [];
          this.gradeList.sort((a, b) => a.casingGrade.localeCompare(b.casingGrade));
          this.updateDisplayedRowsForm(this.gradeList);
          this.tableRef.resetFilters();
          this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
          this.checkIfModified();
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('gradeSaveErrorText')
          } as Alert);
        }
      });
    }
  }

  addRow(): void {
    this.productDataService.getNewCasingGradePk().subscribe({
      next: (response: any) => {
        const newId = response?.data;
        const newRow: CasingGrade = {
          casingGradePk: newId,
          casingGrade: '',
          casingDescription: '',
          rank: ''
        };
        this.locallyAddedGrades.push(newRow);
        this.isSaveEnabled = true;
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  deleteRow(item: CasingGrade): void {
    if (this.productSaveDisabled) {
      return;
    }
    const locallyAddedIndex = this.locallyAddedGrades.findIndex((grade) => grade.casingGradePk === item.casingGradePk);

    if (locallyAddedIndex !== -1) {
      this.locallyAddedGrades.splice(locallyAddedIndex, 1);

      this.alertService.showAlert({
        type: AlertType.success,
        content: this.translationService.translate('gradeDeleteText')
      } as Alert);

      this.updateDisplayedRowsForm([...this.locallyAddedGrades, ...this.gradeList]);
      this.tableRef.resetFilters();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      this.checkIfModified();

      return;
    }
    const payload = { ...item }.casingGradePk;
    this.productDataService.deleteCasingGrade(payload).subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('gradeDeleteText')
        } as Alert);
        this.gradeList = this.gradeList.filter((row) => row.casingGradePk !== item.casingGradePk);
        this.updateDisplayedRowsForm([...this.locallyAddedGrades, ...this.gradeList]);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.isSaveEnabled = false;
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('gradeDeleteErrorText')
        } as Alert);
      }
    });
  }

  showDeleteModal(item: CasingGrade): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.locallyAddedGrades = [];
        this.updateDisplayedRowsForm(this.gradeList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.isSaveEnabled = false;
      }
    }
  }

  getTableRules(grades: CasingGrade[]): void {
    const payload = grades.map((grade) => grade.casingGradePk);
    this.productDataService.checkGradeTableRules(payload).subscribe((response: any) => {
      this.nonDeletableGrades = Array.isArray(response?.data) ? response.data : [];
    });
  }

  canBeDeleted(item: CasingGrade): boolean {
    return Array.isArray(this.nonDeletableGrades) && !this.nonDeletableGrades.includes(item.casingGradePk);
  }

  canDeactivate(): Promise<boolean> {
    if (this.isSaveEnabled) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isSaveEnabled) {
      $event.returnValue = this.translationService.translate('discardChangesText');
    }
  }
}
